import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge, stripAnswerLeak } from '../lib/GameVisuals';
import { ChefHat, ChevronRight, CheckCircle, XCircle, Zap, UtensilsCrossed, Flame } from 'lucide-react';

/* ═══════════════════════════════════════════
   ANIMATED CHEF CHARACTER SVG
   ═══════════════════════════════════════════ */
function ChefCharacterSVG({ size = 140, mood = 'neutral', animate = true, reactTrigger = 0 }) {
  const ref = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!animate || !ref.current) return;
    gsap.to(ref.current, { y: -3, duration: 1.2, yoyo: true, repeat: -1, ease: 'sine.inOut' });
  }, [animate]);

  // React to correct/wrong
  useEffect(() => {
    if (!reactTrigger || !ref.current) return;
    if (mood === 'happy') {
      // Big celebratory jump
      gsap.timeline()
        .to(ref.current, { y: -18, scaleX: 1.08, duration: 0.2, ease: 'power2.out' })
        .to(ref.current, { y: -3, scaleX: 1, duration: 0.4, ease: 'bounce.out' });
    } else if (mood === 'sad') {
      // Head shake
      gsap.timeline()
        .to(ref.current, { x: -6, duration: 0.06 })
        .to(ref.current, { x: 6, duration: 0.06 })
        .to(ref.current, { x: -4, duration: 0.06 })
        .to(ref.current, { x: 4, duration: 0.06 })
        .to(ref.current, { x: 0, duration: 0.1 });
    }
  }, [reactTrigger, mood]);

  const mouthD = mood === 'happy'
    ? 'M42,62 Q50,70 58,62'
    : mood === 'sad'
    ? 'M42,66 Q50,60 58,66'
    : 'M44,64 L56,64';

  const eyeR = mood === 'happy' ? 1.5 : 2.5;
  const cheekOpacity = mood === 'happy' ? 0.5 : 0;

  return (
    <div ref={ref}>
      <svg width={size} height={size * 1.15} viewBox="0 0 100 115">
        {/* Chef hat */}
        <ellipse cx="50" cy="18" rx="24" ry="14" fill="white" stroke="#DDD" strokeWidth="1" />
        <rect x="32" y="14" width="36" height="22" rx="4" fill="white" stroke="#DDD" strokeWidth="1" />
        <ellipse cx="42" cy="14" rx="8" ry="10" fill="white" />
        <ellipse cx="58" cy="14" rx="8" ry="10" fill="white" />
        <ellipse cx="50" cy="10" rx="7" ry="8" fill="white" />
        {/* Head */}
        <circle cx="50" cy="45" r="18" fill="#FFDAB9" />
        {/* Eyes */}
        <circle cx="43" cy="42" r={eyeR} fill="#333" />
        <circle cx="57" cy="42" r={eyeR} fill="#333" />
        {/* Eye sparkle */}
        <circle cx="44" cy="41" r="0.8" fill="white" />
        <circle cx="58" cy="41" r="0.8" fill="white" />
        {/* Eyebrows */}
        <path d={mood === 'sad' ? 'M38,37 Q43,35 46,37' : 'M38,36 Q43,34 46,36'} stroke="#8B6914" strokeWidth="1.5" fill="none" />
        <path d={mood === 'sad' ? 'M54,37 Q57,35 62,37' : 'M54,36 Q57,34 62,36'} stroke="#8B6914" strokeWidth="1.5" fill="none" />
        {/* Cheeks (happy) */}
        <circle cx="36" cy="50" r="4" fill="#FF9B9B" opacity={cheekOpacity} />
        <circle cx="64" cy="50" r="4" fill="#FF9B9B" opacity={cheekOpacity} />
        {/* Nose */}
        <ellipse cx="50" cy="50" rx="3" ry="2.5" fill="#F0C8A0" />
        {/* Mouth */}
        <path d={mouthD} stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        {/* Mustache */}
        <path d="M40,56 Q45,53 50,56 Q55,53 60,56" stroke="#8B6914" strokeWidth="1.8" fill="none" />
        {/* Body / apron */}
        <rect x="32" y="62" width="36" height="40" rx="8" fill="white" stroke="#DDD" strokeWidth="1" />
        {/* Apron pocket */}
        <rect x="40" y="78" width="20" height="12" rx="3" fill="none" stroke="#CCC" strokeWidth="1" />
        {/* Apron strings */}
        <line x1="32" y1="68" x2="24" y2="75" stroke="#CCC" strokeWidth="1.5" />
        <line x1="68" y1="68" x2="76" y2="75" stroke="#CCC" strokeWidth="1.5" />
        {/* Arms */}
        <rect x="18" y="65" width="16" height="8" rx="4" fill="#FFDAB9" />
        <rect x="66" y="65" width="16" height="8" rx="4" fill="#FFDAB9" />
        {/* Hands */}
        <circle cx="18" cy="69" r="5" fill="#FFDAB9" />
        <circle cx="82" cy="69" r="5" fill="#FFDAB9" />
        {/* Spatula in right hand */}
        <rect x="80" y="55" width="3" height="18" rx="1" fill="#8B5A2B" transform="rotate(15, 82, 64)" />
        <rect x="78" y="50" width="7" height="8" rx="2" fill="#666" transform="rotate(15, 82, 54)" />
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════════
   STEAM PARTICLES (rising from pot)
   ═══════════════════════════════════════════ */
function SteamParticles({ active = true, count = 6 }) {
  const particles = useMemo(() =>
    Array.from({ length: count }).map(() => ({
      left: 35 + Math.random() * 30,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 2,
      size: 8 + Math.random() * 12,
    })), [count]);

  if (!active) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`
        @keyframes steamRise { 
          0% { transform: translateY(0) scale(0.5); opacity: 0.6; }
          50% { transform: translateY(-40px) scale(1); opacity: 0.4; }
          100% { transform: translateY(-80px) scale(1.3); opacity: 0; }
        }
        @keyframes steamDrift {
          0%, 100% { margin-left: 0; }
          50% { margin-left: 10px; }
        }
      `}</style>
      {particles.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', left: `${p.left}%`, bottom: '55%',
          width: p.size, height: p.size, borderRadius: '50%',
          background: 'rgba(255,255,255,0.5)',
          animation: `steamRise ${p.duration}s ease-out ${p.delay}s infinite, steamDrift ${p.duration * 0.8}s ease-in-out ${p.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   KITCHEN SCENE SVG
   ═══════════════════════════════════════════ */
function KitchenScene({ stepProgress, totalSteps, recipeName, mood, ingredients, currentStepIdx, reactEvent, reactTrigger, actionText }) {
  const potFill = totalSteps > 0 ? (stepProgress / totalSteps) : 0;
  const potFillHeight = 40 * potFill;
  const potFillColor = potFill > 0.7 ? '#E8590C' : potFill > 0.3 ? '#F59F00' : '#FFD43B';
  const splashRef = useRef(null);
  const smokeRef = useRef(null);
  const actionRef = useRef(null);
  const potRef = useRef(null);
  const burnerRef = useRef(null);

  // React to correct/wrong events with GSAP
  useEffect(() => {
    if (!reactTrigger) return;

    if (reactEvent === 'correct') {
      // POT: wobble + splash
      if (potRef.current) {
        gsap.timeline()
          .to(potRef.current, { y: -4, duration: 0.1, ease: 'power2.out' })
          .to(potRef.current, { y: 0, duration: 0.3, ease: 'bounce.out' });
      }
      // SPLASH particles burst from pot
      if (splashRef.current) {
        const el = splashRef.current;
        el.innerHTML = '';
        for (let i = 0; i < 6; i++) {
          const drop = document.createElement('div');
          const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
          drop.style.cssText = 'position:absolute;border-radius:50%;left:50%;top:0;';
          drop.style.width = (4 + Math.random() * 6) + 'px';
          drop.style.height = drop.style.width;
          drop.style.background = potFillColor || '#FFD43B';
          el.appendChild(drop);
          gsap.fromTo(drop,
            { x: 0, y: 0, opacity: 1, scale: 1 },
            { x: Math.cos(angle) * (15 + Math.random() * 20), y: Math.sin(angle) * (20 + Math.random() * 15), opacity: 0, scale: 0.3, duration: 0.6, ease: 'power2.out' }
          );
        }
      }
      // ACTION TEXT floats up into pot area
      if (actionRef.current && actionText) {
        gsap.fromTo(actionRef.current,
          { y: 30, opacity: 0, scale: 0.8 },
          { y: 0, opacity: 1, scale: 1, duration: 0.3, ease: 'back.out(2)' }
        );
        gsap.to(actionRef.current, { y: -25, opacity: 0, scale: 0.6, duration: 0.5, delay: 0.5, ease: 'power2.in' });
      }
    } else if (reactEvent === 'wrong') {
      // SMOKE puff from pot
      if (smokeRef.current) {
        const el = smokeRef.current;
        el.innerHTML = '';
        for (let i = 0; i < 4; i++) {
          const puff = document.createElement('div');
          puff.style.cssText = 'position:absolute;border-radius:50%;left:50%;top:0;background:rgba(80,80,80,0.4);';
          puff.style.width = (12 + Math.random() * 10) + 'px';
          puff.style.height = puff.style.width;
          el.appendChild(puff);
          gsap.fromTo(puff,
            { x: (Math.random() - 0.5) * 10, y: 0, opacity: 0.5, scale: 0.5 },
            { x: (Math.random() - 0.5) * 30, y: -(30 + Math.random() * 20), opacity: 0, scale: 1.5, duration: 0.8, ease: 'power1.out' }
          );
        }
      }
      // BURNER flare
      if (burnerRef.current) {
        gsap.timeline()
          .to(burnerRef.current, { opacity: 1, scale: 1.3, duration: 0.15 })
          .to(burnerRef.current, { opacity: 0.6, scale: 1, duration: 0.4 });
      }
      // POT shake
      if (potRef.current) {
        gsap.fromTo(potRef.current, { x: 0 }, { x: 4, duration: 0.05, repeat: 4, yoyo: true });
      }
    }
  }, [reactTrigger, reactEvent, potFillColor, actionText]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 900 320" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="kitchen-wall" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFF8E7" />
            <stop offset="100%" stopColor="#FFEDC2" />
          </linearGradient>
          <linearGradient id="counter-top" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D4A76A" />
            <stop offset="30%" stopColor="#C49660" />
            <stop offset="100%" stopColor="#B08050" />
          </linearGradient>
          <linearGradient id="counter-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8B6914" />
            <stop offset="100%" stopColor="#6B4F0E" />
          </linearGradient>
          <linearGradient id="pot-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#888" />
            <stop offset="50%" stopColor="#666" />
            <stop offset="100%" stopColor="#555" />
          </linearGradient>
          <linearGradient id="stove-face" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#444" />
            <stop offset="100%" stopColor="#333" />
          </linearGradient>
          <radialGradient id="burner-glow">
            <stop offset="0%" stopColor="#FF6B35" stopOpacity="0.6" />
            <stop offset="60%" stopColor="#FF4500" stopOpacity="0.3" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>

        {/* Wall */}
        <rect width="900" height="200" fill="url(#kitchen-wall)" />

        {/* Wall tiles (subtle pattern) */}
        {Array.from({ length: 12 }).map((_, i) => (
          <g key={`tile-${i}`}>
            <rect x={75 * i} y="80" width="74" height="40" rx="2" fill="none" stroke="rgba(200,180,140,0.3)" strokeWidth="1" />
            <rect x={37.5 + 75 * i} y="120" width="74" height="40" rx="2" fill="none" stroke="rgba(200,180,140,0.3)" strokeWidth="1" />
          </g>
        ))}

        {/* Window */}
        <rect x="620" y="20" width="120" height="100" rx="6" fill="#87CEEB" stroke="#B08050" strokeWidth="4" />
        <line x1="680" y1="20" x2="680" y2="120" stroke="#B08050" strokeWidth="3" />
        <line x1="620" y1="70" x2="740" y2="70" stroke="#B08050" strokeWidth="3" />
        {/* Window curtains */}
        <path d="M618,20 Q625,35 618,50 Q625,65 618,80" fill="none" stroke="#E8590C" strokeWidth="3" opacity="0.5" />
        <path d="M742,20 Q735,35 742,50 Q735,65 742,80" fill="none" stroke="#E8590C" strokeWidth="3" opacity="0.5" />
        {/* Sun through window */}
        <circle cx="700" cy="45" r="12" fill="#FFD43B" opacity="0.7" />
        {/* Clouds */}
        <ellipse cx="650" cy="40" rx="15" ry="6" fill="white" opacity="0.7" />
        <ellipse cx="660" cy="36" rx="10" ry="5" fill="white" opacity="0.6" />

        {/* Shelf */}
        <rect x="50" y="40" width="200" height="8" rx="2" fill="#B08050" />
        <rect x="50" y="40" width="200" height="3" rx="1" fill="#C49660" />
        {/* Items on shelf */}
        <rect x="70" y="12" width="16" height="28" rx="3" fill="#E03131" opacity="0.7" />
        <rect x="70" y="8" width="16" height="6" rx="2" fill="#C0392B" opacity="0.7" />
        <rect x="100" y="18" width="14" height="22" rx="2" fill="#2F9E44" opacity="0.6" />
        <circle cx="140" cy="30" r="10" fill="#FFD43B" opacity="0.5" />
        <rect x="170" y="15" width="18" height="25" rx="3" fill="#4C6EF5" opacity="0.5" />
        <rect x="200" y="20" width="12" height="20" rx="2" fill="#E8590C" opacity="0.6" />

        {/* Second shelf */}
        <rect x="300" y="55" width="150" height="7" rx="2" fill="#B08050" />
        <rect x="300" y="55" width="150" height="3" rx="1" fill="#C49660" />
        {/* Hanging utensils */}
        <line x1="320" y1="62" x2="320" y2="85" stroke="#888" strokeWidth="2" />
        <circle cx="320" cy="88" r="6" fill="none" stroke="#888" strokeWidth="2" />
        <line x1="355" y1="62" x2="355" y2="90" stroke="#888" strokeWidth="2" />
        <rect x="350" y="90" width="10" height="3" rx="1" fill="#888" />
        <line x1="390" y1="62" x2="390" y2="82" stroke="#888" strokeWidth="2" />
        <path d="M385,82 Q390,95 395,82" fill="none" stroke="#888" strokeWidth="2" />

        {/* Counter top */}
        <rect x="0" y="195" width="900" height="14" rx="2" fill="url(#counter-top)" />
        <rect x="0" y="195" width="900" height="4" fill="rgba(255,255,255,0.15)" />

        {/* Counter front */}
        <rect x="0" y="209" width="900" height="111" fill="url(#counter-front)" />
        {/* Cabinet doors */}
        {[40, 190, 340, 490, 640].map((x, i) => (
          <g key={`cab-${i}`}>
            <rect x={x} y="220" width="120" height="85" rx="4" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
            <circle cx={x + 100} cy="262" r="4" fill="rgba(255,255,255,0.15)" />
          </g>
        ))}

        {/* STOVE */}
        <rect x="350" y="130" width="200" height="68" rx="4" fill="url(#stove-face)" />
        <rect x="355" y="133" width="190" height="3" fill="rgba(255,255,255,0.08)" />
        {/* Stove knobs */}
        {[390, 420, 480, 510].map((kx, i) => (
          <g key={`knob-${i}`}>
            <circle cx={kx} cy="185" r="6" fill="#555" stroke="#444" strokeWidth="1" />
            <line x1={kx} y1="181" x2={kx} y2="185" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </g>
        ))}

        {/* Burner (active) */}
        <g ref={burnerRef}>
        <ellipse cx="450" cy="155" rx="40" ry="8" fill="url(#burner-glow)">
          <animate attributeName="rx" values="40;42;40" dur="1.5s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="450" cy="155" rx="35" ry="6" fill="none" stroke="#FF6B35" strokeWidth="1.5" opacity="0.4">
          <animate attributeName="opacity" values="0.4;0.7;0.4" dur="1s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="450" cy="155" rx="25" ry="4" fill="none" stroke="#FF4500" strokeWidth="1" opacity="0.3">
          <animate attributeName="opacity" values="0.3;0.6;0.3" dur="0.8s" repeatCount="indefinite" />
        </ellipse>
        </g>

        {/* POT on stove */}
        <g ref={potRef} transform="translate(410, 110)">
          {/* Pot handles */}
          <ellipse cx="0" cy="25" rx="8" ry="4" fill="none" stroke="#666" strokeWidth="3" />
          <ellipse cx="80" cy="25" rx="8" ry="4" fill="none" stroke="#666" strokeWidth="3" />
          {/* Pot body */}
          <path d="M5,10 L5,45 Q5,50 10,50 L70,50 Q75,50 75,45 L75,10 Z" fill="url(#pot-body)" />
          <path d="M5,10 L75,10" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
          {/* Pot contents (fills based on progress) */}
          {potFill > 0 && (
            <rect x="7" y={48 - potFillHeight} width="66" height={potFillHeight} fill={potFillColor} opacity="0.7" rx="2">
              <animate attributeName="opacity" values="0.7;0.85;0.7" dur="2s" repeatCount="indefinite" />
            </rect>
          )}
          {/* Pot rim */}
          <ellipse cx="40" cy="10" rx="37" ry="6" fill="#777" />
          <ellipse cx="40" cy="10" rx="34" ry="5" fill={potFill > 0 ? potFillColor : '#555'} opacity={potFill > 0 ? 0.8 : 1} />
          {/* Lid (slightly ajar if cooking) */}
          {potFill > 0 && potFill < 1 && (
            <g>
              <ellipse cx="42" cy="6" rx="34" ry="5" fill="#888" transform="rotate(-5, 42, 6)" />
              <circle cx="42" cy="2" r="4" fill="#666" />
            </g>
          )}
        </g>

        {/* Cutting board with ingredients */}
        <g transform="translate(590, 160)">
          <rect x="0" y="5" width="80" height="35" rx="4" fill="#D4A76A" />
          <rect x="2" y="7" width="76" height="31" rx="3" fill="#C49660" />
          {/* Wood grain lines */}
          <line x1="10" y1="12" x2="70" y2="12" stroke="rgba(139,105,20,0.15)" strokeWidth="1" />
          <line x1="10" y1="22" x2="70" y2="22" stroke="rgba(139,105,20,0.15)" strokeWidth="1" />
          <line x1="10" y1="32" x2="70" y2="32" stroke="rgba(139,105,20,0.15)" strokeWidth="1" />
        </g>

        {/* Recipe name plate */}
        <g transform="translate(100, 100)">
          <rect x="0" y="0" width="180" height="36" rx="8" fill="rgba(255,255,255,0.85)" stroke="rgba(232,89,12,0.2)" strokeWidth="1.5" />
          <text x="90" y="24" textAnchor="middle" fontSize="14" fontWeight="700" fill="#E8590C" fontFamily="Fredoka">
            {recipeName || 'Le Chef'}
          </text>
        </g>
      </svg>

      <SteamParticles active={potFill > 0} count={potFill > 0.5 ? 8 : 4} />

      {/* Splash particles (correct answer — burst from pot) */}
      <div ref={splashRef} style={{
        position: 'absolute', left: '50%', top: '32%', pointerEvents: 'none', zIndex: 8,
      }} />

      {/* Smoke puffs (wrong answer — from pot) */}
      <div ref={smokeRef} style={{
        position: 'absolute', left: '50%', top: '28%', pointerEvents: 'none', zIndex: 8,
      }} />

      {/* Action text that floats up on correct */}
      {actionText && reactEvent === 'correct' && (
        <div ref={actionRef} style={{
          position: 'absolute', left: '50%', top: '22%', transform: 'translateX(-50%)',
          fontFamily: 'Fredoka', fontSize: '1.1rem', fontWeight: 800, color: '#2F9E44',
          background: 'rgba(47,158,68,0.12)', padding: '4px 14px', borderRadius: 12,
          border: '2px solid rgba(47,158,68,0.25)', whiteSpace: 'nowrap',
          pointerEvents: 'none', zIndex: 9,
        }}>
          {actionText}
        </div>
      )}

      {/* Chef character (positioned on left of scene) */}
      <div style={{
        position: 'absolute', left: '3%', bottom: '28%',
        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))',
        zIndex: 5,
      }}>
        <ChefCharacterSVG size={120} mood={mood} animate={true} reactTrigger={reactTrigger} />
      </div>

      {/* Step progress dots overlay */}
      <div style={{
        position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: 6, zIndex: 10,
        background: 'rgba(255,255,255,0.85)', padding: '6px 14px', borderRadius: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={{
            width: i === currentStepIdx ? 24 : 10, height: 10, borderRadius: 5,
            background: i < currentStepIdx ? '#2F9E44' : i === currentStepIdx ? '#E8590C' : 'rgba(0,0,0,0.12)',
            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            boxShadow: i === currentStepIdx ? '0 0 8px rgba(232,89,12,0.4)' : 'none',
          }} />
        ))}
      </div>

      {/* Ingredient chips along the right side */}
      {ingredients && ingredients.length > 0 && (
        <div style={{
          position: 'absolute', right: 8, top: 8,
          display: 'flex', flexDirection: 'column', gap: 4, zIndex: 10,
        }}>
          {ingredients.slice(0, 5).map((ing, i) => (
            <div key={i} style={{
              padding: '3px 10px', borderRadius: 12,
              background: 'rgba(255,255,255,0.85)',
              border: '1px solid rgba(232,89,12,0.15)',
              fontFamily: 'Fredoka', fontSize: '0.75rem', fontWeight: 600,
              color: '#E8590C', whiteSpace: 'nowrap',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              {ing}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   TUTORIAL SCREEN
   ═══════════════════════════════════════════ */
function TutorialScreen({ onStart, recipes }) {
  const sampleRecipe = recipes[0];
  return (
    <div data-testid="lechef-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #FFF4E6 0%, #FFE8CC 40%, #F0F4F8 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 760, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <ChefHat size={48} color="#E8590C" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            LE CHEF
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Follow recipe steps in French to cook Canadian dishes!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(232,89,12,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.3rem' }}>
              <ChefHat size={40} color="#E8590C" />
            </div>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#E8590C', marginBottom: '0.3rem' }}>
              Read
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Read the French instruction for each cooking step
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(47,158,68,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <CheckCircle size={40} color="#2F9E44" style={{ marginBottom: '0.3rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#2F9E44', marginBottom: '0.3rem' }}>
              Choose
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Pick the correct action from 4 choices
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Flame size={40} color="#F59F00" style={{ marginBottom: '0.3rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Cook!
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Complete all steps to finish the dish!
            </div>
          </div>
        </div>

        {sampleRecipe && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: '#555', marginBottom: '0.5rem' }}>
              First recipe:
            </div>
            <div style={{
              display: 'inline-block', padding: '0.5rem 1.5rem', borderRadius: '0.75rem',
              background: 'rgba(255,255,255,0.9)', border: '2px solid rgba(232,89,12,0.2)',
              fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700, color: '#E8590C',
            }}>
              {sampleRecipe.recipe_name_fr}
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1rem', color: '#888', marginTop: '0.3rem' }}>
              {sampleRecipe.total_steps || sampleRecipe.steps?.length} steps
            </div>
          </div>
        )}

        <button data-testid="lechef-tutorial-start" onClick={onStart} className="btn-arcade btn-primary"
          style={{ fontSize: '1.8rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem', borderBottomWidth: 8, background: '#E8590C', borderBottomColor: '#C2410C' }}>
          <ChevronRight size={32} /> Let's Cook!
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   RECIPE INTRO SCREEN
   ═══════════════════════════════════════════ */
function RecipeIntro({ recipe, recipeNum, totalRecipes, onReady }) {
  const [countdown, setCountdown] = useState(4);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          setTimeout(() => onReadyRef.current(), 0);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="lechef-recipe-intro" style={{
      position: 'absolute', inset: 0, zIndex: 50,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.97)', borderRadius: '1.5rem', padding: '2rem 3rem',
        textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        animation: 'chefPopIn 0.3s ease-out', maxWidth: 650,
      }}>
        <style>{`@keyframes chefPopIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.1rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.25rem' }}>
          RECIPE {recipeNum} of {totalRecipes}
        </div>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '2.2rem', fontWeight: 700, color: '#E8590C', marginBottom: '0.5rem',
        }}>
          {recipe.recipe_name_fr}
        </div>
        <div style={{ fontFamily: 'Nunito', fontSize: '1.1rem', color: '#888', fontWeight: 600, marginBottom: '0.75rem', fontStyle: 'italic' }}>
          {recipe.recipe_name_en}
        </div>

        {recipe.cultural_origin && (
          <div style={{
            padding: '0.5rem 1rem', borderRadius: '0.75rem',
            background: 'rgba(232,89,12,0.06)', border: '1.5px solid rgba(232,89,12,0.15)',
            fontFamily: 'Nunito', fontSize: '1rem', color: '#666', marginBottom: '1rem',
          }}>
            {recipe.cultural_origin}
          </div>
        )}

        {recipe.ingredients_needed && recipe.ingredients_needed.length > 0 && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1rem', color: '#555', marginBottom: '0.4rem', fontWeight: 700 }}>
              Ingredients:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'center' }}>
              {recipe.ingredients_needed.map((ing, i) => (
                <span key={i} style={{
                  padding: '0.25rem 0.75rem', borderRadius: '2rem',
                  background: 'rgba(245,159,0,0.1)', fontFamily: 'Fredoka', fontSize: '0.95rem',
                  fontWeight: 600, color: '#E8590C', border: '1px solid rgba(232,89,12,0.15)',
                }}>
                  {ing}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{
          padding: '0.2rem 0.75rem', borderRadius: '2rem', display: 'inline-block',
          background: 'rgba(232,89,12,0.1)', fontFamily: 'Fredoka', fontSize: '1rem',
          fontWeight: 700, color: '#E8590C', marginBottom: '0.5rem',
        }}>
          {recipe.total_steps || recipe.steps?.length || 0} steps
        </div>

        <div style={{ marginTop: '0.75rem', fontFamily: 'Fredoka', fontSize: '2.5rem', fontWeight: 700, color: '#E8590C' }}>
          {countdown}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN LE CHEF GAME
   ═══════════════════════════════════════════ */
export function LeChef({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('le_chef'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const chefItems = items.filter(i => i.steps && i.steps.length > 0);
  const maxRecipes = Math.min(settings.rounds, chefItems.length);

  const [showTutorial, setShowTutorial] = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const [recipeIdx, setRecipeIdx] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [stepsCompleted, setStepsCompleted] = useState(0);
  const [showDishComplete, setShowDishComplete] = useState(false);
  const [showConfetti, setShowConfetti] = useState(0);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [recipePerfect, setRecipePerfect] = useState(true);
  const [chefMood, setChefMood] = useState('neutral');
  const [reactEvent, setReactEvent] = useState(null); // 'correct' | 'wrong' | null
  const [reactTrigger, setReactTrigger] = useState(0);
  const [actionText, setActionText] = useState(null);

  const scoreRef = useRef(null);
  const endedRef = useRef(false);
  const reviewRef = useRef([]);
  const scoreValRef = useRef(0);
  const bestStreakValRef = useRef(0);
  const optionRefs = useRef([]);
  const instructionRef = useRef(null);
  const feedbackRef = useRef(null);
  scoreValRef.current = score;
  bestStreakValRef.current = bestStreak;

  const currentRecipe = chefItems[recipeIdx];
  const currentStep = currentRecipe?.steps?.[stepIdx];
  const totalGameSteps = chefItems.slice(0, maxRecipes).reduce((sum, r) => sum + (r.steps?.length || 0), 0);

  // Shuffle options when step changes
  useEffect(() => {
    if (!currentStep) return;
    const options = [currentStep.correct_action, ...currentStep.distractors];
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    setShuffledOptions(options);
    setFeedback(null);
    setSelectedAnswer(null);
    setChefMood('neutral');
    setReactEvent(null);
    setActionText(null);

    // Animate instruction card entrance
    if (instructionRef.current) {
      gsap.fromTo(instructionRef.current,
        { y: 20, opacity: 0, scale: 0.95 },
        { y: 0, opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.7)' }
      );
    }
    // Animate option buttons entrance (staggered)
    setTimeout(() => {
      optionRefs.current.forEach((el, i) => {
        if (el) {
          gsap.fromTo(el,
            { x: -30, opacity: 0 },
            { x: 0, opacity: 1, duration: 0.3, delay: i * 0.08, ease: 'power2.out' }
          );
        }
      });
    }, 100);
  }, [recipeIdx, stepIdx]);

  const handleTutorialDone = useCallback(() => {
    setShowTutorial(false);
  }, []);

  const handleIntroDone = useCallback(() => {
    setShowIntro(false);
  }, []);

  const endGame = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    setShowConfetti(c => c + 1);
    playSound('win');
    const finalScore = scoreValRef.current;
    const finalBestStreak = bestStreakValRef.current;
    const review = [...reviewRef.current];
    for (let i = review.length; i < maxRecipes && i < chefItems.length; i++) {
      const r = chefItems[i]; if (!r) break;
      review.push({
        recipeName: r.recipe_name_fr,
        recipeNameEn: r.recipe_name_en,
        culturalOrigin: r.cultural_origin,
        steps: (r.steps || []).map(s => ({
          instruction_fr: s.instruction_fr,
          instruction_en: s.instruction_en,
          correct_action: s.correct_action,
          chosen: null,
          wasCorrect: null,
        })),
        wasComplete: false,
      });
    }
    setTimeout(() => onGameEnd({
      team1Score: finalScore, team2Score: 0,
      totalQuestions: totalGameSteps,
      accuracy: totalGameSteps > 0 ? (finalScore / totalGameSteps) * 100 : 0,
      bestStreak: finalBestStreak,
      questionReview: review, reviewType: 'recipe',
    }), 1500);
  }, [onGameEnd, playSound, maxRecipes, chefItems, totalGameSteps]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => endGame();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [endGame]);

  const advanceStep = useCallback(() => {
    const recipe = chefItems[recipeIdx];
    if (!recipe) return;
    const nextStep = stepIdx + 1;
    if (nextStep >= recipe.steps.length) {
      reviewRef.current[reviewRef.current.length - 1].wasComplete = true;
      setShowDishComplete(true);
      setShowConfetti(c => c + 1);
      setChefMood('happy');
      playSound('match');
      setTimeout(() => {
        setShowDishComplete(false);
        const nextRecipe = recipeIdx + 1;
        if (nextRecipe >= maxRecipes) {
          endGame();
        } else {
          setRecipeIdx(nextRecipe);
          setStepIdx(0);
          setRecipePerfect(true);
        }
      }, 2200);
    } else {
      setStepIdx(nextStep);
    }
  }, [recipeIdx, stepIdx, chefItems, maxRecipes, endGame, playSound]);

  const handleAnswer = useCallback((answer, btnEl) => {
    if (feedback) return;
    playSound('click');
    setSelectedAnswer(answer);
    const isCorrect = answer === currentStep.correct_action;

    if (isCorrect) {
      playSound('match');
      setFeedback('correct');
      setChefMood('happy');
      setActionText(currentStep.correct_action);
      setReactEvent('correct');
      setReactTrigger(t => t + 1);
      setScore(s => s + 1);
      setStepsCompleted(s => s + 1);
      setStreak(s => {
        const next = s + 1;
        setBestStreak(b => Math.max(b, next));
        return next;
      });
      // Animate score pop
      if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.5 }, { scale: 1, duration: 0.5, ease: 'elastic.out(1,0.4)' });
      // Animate correct button glow
      if (btnEl) {
        gsap.fromTo(btnEl, { scale: 1.05 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
      }
      // Feedback pop-in
      if (feedbackRef.current) {
        gsap.fromTo(feedbackRef.current, { scale: 0.5, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(2)' });
      }

      const currentReview = reviewRef.current[reviewRef.current.length - 1];
      if (currentReview) {
        currentReview.steps.push({
          instruction_fr: currentStep.instruction_fr,
          instruction_en: currentStep.instruction_en,
          correct_action: currentStep.correct_action,
          chosen: answer, wasCorrect: true,
        });
      }
      setTimeout(() => advanceStep(), 1200);
    } else {
      playSound('wrong');
      setFeedback('wrong');
      setChefMood('sad');
      setReactEvent('wrong');
      setReactTrigger(t => t + 1);
      setActionText(null);
      setStreak(0);
      setRecipePerfect(false);
      setStepsCompleted(s => s + 1);
      // Shake the wrong button
      if (btnEl) {
        gsap.fromTo(btnEl, { x: 0 }, { x: 12, duration: 0.06, repeat: 5, yoyo: true, ease: 'power2.inOut' });
      }
      if (feedbackRef.current) {
        gsap.fromTo(feedbackRef.current, { scale: 0.5, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(2)' });
      }

      const currentReview = reviewRef.current[reviewRef.current.length - 1];
      if (currentReview) {
        currentReview.steps.push({
          instruction_fr: currentStep.instruction_fr,
          instruction_en: currentStep.instruction_en,
          correct_action: currentStep.correct_action,
          chosen: answer, wasCorrect: false,
        });
      }
      setTimeout(() => advanceStep(), 2000);
    }
  }, [feedback, currentStep, playSound, advanceStep]);

  // Initialize review entry for each new recipe
  useEffect(() => {
    if (showIntro || showTutorial || !currentRecipe) return;
    if (reviewRef.current.length <= recipeIdx) {
      reviewRef.current.push({
        recipeName: currentRecipe.recipe_name_fr,
        recipeNameEn: currentRecipe.recipe_name_en,
        culturalOrigin: currentRecipe.cultural_origin,
        steps: [],
        wasComplete: false,
      });
    }
  }, [recipeIdx, showIntro, showTutorial, currentRecipe]);

  if (!currentRecipe && !showTutorial) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text-muted)', fontSize: '1.5rem', fontFamily: 'Fredoka',
      }}>
        No recipe content available
      </div>
    );
  }

  const overallProgress = totalGameSteps > 0 ? (stepsCompleted / totalGameSteps) * 100 : 0;

  return (
    <div data-testid="lechef-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: '#F0F4F8',
      position: 'relative', overflow: 'hidden',
    }}>
      <Confetti trigger={showConfetti} />

      {showTutorial && <TutorialScreen onStart={handleTutorialDone} recipes={chefItems.slice(0, maxRecipes)} />}

      {showIntro && currentRecipe && (
        <RecipeIntro
          recipe={currentRecipe}
          recipeNum={recipeIdx + 1}
          totalRecipes={maxRecipes}
          onReady={handleIntroDone}
        />
      )}

      {/* Dish Complete Overlay */}
      {showDishComplete && currentRecipe && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 50,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.97)', borderRadius: '1.5rem', padding: '2rem 3rem',
            textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            animation: 'chefPopIn 0.4s ease-out',
          }}>
            <UtensilsCrossed size={56} color="#2F9E44" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700, color: '#2F9E44', marginBottom: '0.25rem' }}>
              Dish Complete!
            </div>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.6rem', fontWeight: 700, color: '#E8590C' }}>
              {currentRecipe.recipe_name_fr}
            </div>
            {recipePerfect && (
              <div style={{ fontFamily: 'Nunito', fontSize: '1.1rem', color: '#F59F00', fontWeight: 700, marginTop: '0.4rem' }}>
                Perfect recipe!
              </div>
            )}
          </div>
        </div>
      )}

      <GameHeader title="LE CHEF" icon={<ChefHat size={24} color="#E8590C" />} color="#E8590C">
        <ScoreBadge label="STEPS" value={`${score}/${totalGameSteps}`} color="#2F9E44" scoreRef={scoreRef} />
        {streak >= 2 && (
          <div data-testid="chef-streak-badge" style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: 'rgba(245,159,0,0.1)', padding: '0.2rem 0.75rem', borderRadius: '2rem',
            border: '2px solid rgba(245,159,0,0.3)',
          }}>
            <Zap size={18} color="#F59F00" fill="#F59F00" />
            <span style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 800, color: '#F59F00' }}>{streak}</span>
          </div>
        )}
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: 'var(--text-muted)', fontWeight: 600 }}>
          Recipe {recipeIdx + 1}/{maxRecipes}
        </div>
      </GameHeader>

      {/* Progress bar */}
      <div style={{ height: 6, background: 'rgba(0,0,0,0.06)' }}>
        <div style={{
          height: '100%', width: `${overallProgress}%`, background: 'linear-gradient(90deg, #E8590C, #F59F00)',
          transition: 'width 0.5s ease', borderRadius: '0 3px 3px 0',
        }} />
      </div>

      {currentRecipe && currentStep && !showDishComplete && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Kitchen Scene — top portion */}
          <div style={{
            flex: '0 0 42%', position: 'relative',
            borderBottom: '3px solid rgba(0,0,0,0.06)',
          }}>
            <KitchenScene
              stepProgress={stepIdx}
              totalSteps={currentRecipe.steps.length}
              recipeName={currentRecipe.recipe_name_fr}
              mood={chefMood}
              ingredients={currentRecipe.ingredients_needed}
              currentStepIdx={stepIdx}
              reactEvent={reactEvent}
              reactTrigger={reactTrigger}
              actionText={actionText}
            />
          </div>

          {/* Question Area — bottom portion */}
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: '0.75rem', padding: '0.75rem 1.5rem',
            background: 'linear-gradient(180deg, #FFF9F0 0%, #F0F4F8 100%)',
          }}>
            {/* French instruction */}
            <div ref={instructionRef} data-testid="step-instruction" style={{
              fontSize: 'clamp(1.3rem, 2.8vw, 1.8rem)', fontWeight: 700, fontFamily: 'Fredoka',
              color: 'var(--text-primary)', textAlign: 'center',
              background: 'rgba(255,255,255,0.95)', padding: '0.75rem 2rem', borderRadius: '1.25rem',
              border: '2px solid rgba(232,89,12,0.15)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              maxWidth: 850,
            }}>
              {stripAnswerLeak(currentStep.instruction_fr, currentStep.correct_action)}
            </div>

            {/* English helper */}
            {currentStep.instruction_en && (
              <div style={{
                fontFamily: 'Nunito', fontSize: 'clamp(0.85rem, 1.5vw, 1rem)', color: '#999',
                fontWeight: 600, fontStyle: 'italic',
              }}>
                {stripAnswerLeak(currentStep.instruction_en, currentStep.correct_action)}
              </div>
            )}

            {/* Answer options */}
            <div data-testid="answer-options" style={{
              display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.6rem',
              maxWidth: 700, width: '100%',
            }}>
              {shuffledOptions.map((option, idx) => {
                const isSelected = selectedAnswer === option;
                const isCorrectAnswer = option === currentStep.correct_action;
                let bg = 'white';
                let borderColor = 'rgba(0,0,0,0.08)';
                let textColor = 'var(--text-primary)';
                let shadow = '0 3px 12px rgba(0,0,0,0.06)';

                if (feedback) {
                  if (isCorrectAnswer) {
                    bg = 'rgba(47,158,68,0.12)';
                    borderColor = '#2F9E44';
                    textColor = '#2F9E44';
                    shadow = '0 0 16px rgba(47,158,68,0.2)';
                  } else if (isSelected && !isCorrectAnswer) {
                    bg = 'rgba(224,49,49,0.08)';
                    borderColor = '#E03131';
                    textColor = '#E03131';
                  } else {
                    bg = 'rgba(0,0,0,0.02)';
                    borderColor = 'rgba(0,0,0,0.04)';
                    textColor = 'var(--text-muted)';
                  }
                }

                return (
                  <button key={idx}
                    ref={el => optionRefs.current[idx] = el}
                    data-testid={`chef-option-${idx}`}
                    onClick={(e) => handleAnswer(option, e.currentTarget)}
                    disabled={!!feedback}
                    style={{
                      padding: '0.8rem 1rem', borderRadius: '1rem',
                      background: bg, border: `2px solid ${borderColor}`,
                      borderBottom: feedback ? `2px solid ${borderColor}` : '5px solid var(--border)',
                      color: textColor,
                      fontSize: 'clamp(1rem, 1.8vw, 1.3rem)', fontWeight: 700, fontFamily: 'Fredoka',
                      cursor: feedback ? 'default' : 'pointer', touchAction: 'manipulation',
                      boxShadow: shadow,
                      transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
                      display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center',
                      opacity: feedback && !isSelected && !isCorrectAnswer ? 0.5 : 1,
                    }}
                    onMouseDown={e => { if (!feedback) e.currentTarget.style.transform = 'translateY(3px)'; }}
                    onMouseUp={e => e.currentTarget.style.transform = ''}
                    onMouseLeave={e => e.currentTarget.style.transform = ''}
                  >
                    {feedback && isCorrectAnswer && <CheckCircle size={20} color="#2F9E44" />}
                    {feedback && isSelected && !isCorrectAnswer && <XCircle size={20} color="#E03131" />}
                    {option}
                  </button>
                );
              })}
            </div>

            {/* Feedback text */}
            {feedback === 'correct' && (
              <div ref={feedbackRef} data-testid="correct-feedback" style={{
                fontSize: 'clamp(1rem, 2vw, 1.3rem)', color: '#2F9E44', fontWeight: 700, fontFamily: 'Fredoka',
                background: 'rgba(47,158,68,0.08)', padding: '0.4rem 1.25rem', borderRadius: '1rem',
                border: '2px solid rgba(47,158,68,0.15)',
              }}>
                Correct!
              </div>
            )}
            {feedback === 'wrong' && (
              <div ref={feedbackRef} data-testid="wrong-feedback" style={{
                fontSize: 'clamp(0.9rem, 1.8vw, 1.15rem)', color: '#E03131', fontWeight: 700, fontFamily: 'Fredoka',
                background: 'rgba(224,49,49,0.06)', padding: '0.4rem 1.25rem', borderRadius: '1rem',
                border: '2px solid rgba(224,49,49,0.15)',
              }}>
                Correct answer: <span style={{ color: '#2F9E44' }}>{currentStep.correct_action}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes chefPopIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
    </div>
  );
}
